/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { InquirySubmitRequest } from "../models/InquirySubmitRequest";
import type { PublicFormLoadRequest } from "../models/PublicFormLoadRequest";
import type { PublicFormSubmitRequest } from "../models/PublicFormSubmitRequest";
import type { PublicFormSubmitResponse } from "../models/PublicFormSubmitResponse";
import type { PublicFormView } from "../models/PublicFormView";
import type { PublicPaymentSession } from "../models/PublicPaymentSession";
import type { PublicPayrollLink } from "../models/PublicPayrollLink";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class PublicFormsService {
  /**
   * Load Public Form
   * The form for a link, filled with what is on file where the form shows it.
   * @returns PublicFormView Successful Response
   * @throws ApiError
   */
  public static loadPublicForm({
    requestBody,
  }: {
    requestBody: PublicFormLoadRequest;
  }): CancelablePromise<PublicFormView> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/public/forms/load",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Submit Public Form
   * Save the answers in one transaction. Nothing is saved if anything is wrong.
   * @returns PublicFormSubmitResponse Successful Response
   * @throws ApiError
   */
  public static submitPublicForm({
    requestBody,
  }: {
    requestBody: PublicFormSubmitRequest;
  }): CancelablePromise<PublicFormSubmitResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/public/forms/submit",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Start Public Form Payment
   * Secrets for Stripe's Payment Element: see, add and remove saved methods.
   * @returns PublicPaymentSession Successful Response
   * @throws ApiError
   */
  public static startPublicFormPayment({
    requestBody,
  }: {
    requestBody: PublicFormLoadRequest;
  }): CancelablePromise<PublicPaymentSession> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/public/forms/payment_session",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Start Public Form Payroll Link
   * A tutor's link to Stripe to add their payroll bank; it returns to the form.
   * @returns PublicPayrollLink Successful Response
   * @throws ApiError
   */
  public static startPublicFormPayrollLink({
    requestBody,
  }: {
    requestBody: PublicFormLoadRequest;
  }): CancelablePromise<PublicPayrollLink> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/public/forms/payroll_link",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Load Inquiry Form
   * A website inquiry form: the form itself, empty, for a new family.
   * @returns PublicFormView Successful Response
   * @throws ApiError
   */
  public static loadInquiryForm({
    publicId,
  }: {
    publicId: string;
  }): CancelablePromise<PublicFormView> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/public/forms/inquiry/{public_id}",
      path: {
        public_id: publicId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Submit Inquiry Form
   * Save an inquiry: a new parent and students, in one transaction. The
   * organization is told after it is saved.
   * @returns PublicFormSubmitResponse Successful Response
   * @throws ApiError
   */
  public static submitInquiryForm({
    publicId,
    requestBody,
  }: {
    publicId: string;
    requestBody: InquirySubmitRequest;
  }): CancelablePromise<PublicFormSubmitResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/public/forms/inquiry/{public_id}/submit",
      path: {
        public_id: publicId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
